import React, { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, BarChart2, PlusCircle, TrendingUp, Wallet, Menu, X, LogOut } from 'lucide-react';

// ── Config ────────────────────────────────────────────────────────────────────
export const API = 'https://api.naijapredict.net';

export async function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('np_creator_token');
  const res = await fetch(API + path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...opts.headers,
    },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

// ── Theme ─────────────────────────────────────────────────────────────────────
const T = {
  bg:      '#07090a',
  bg2:     '#0d1114',
  bg3:     '#131a1e',
  border:  'rgba(255,255,255,.06)',
  text:    '#e8eaeb',
  muted:   '#4a5a63',
  green:   '#00c566',
  yellow:  '#f5c842',
  orange:  '#ff6b35',
  red:     '#ff4444',
};

// ── Auth Context ──────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('np_creator_token');
    if (!token) return setLoading(false);
    apiFetch('/api/creator/me')
      .then(setCreator)
      .catch(() => localStorage.removeItem('np_creator_token'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:T.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'sans-serif', color:T.green }}>
      Loading...
    </div>
  );

  return (
    <AuthCtx.Provider value={{
      creator, setCreator,
      logout: () => { localStorage.removeItem('np_creator_token'); setCreator(null); }
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

// ── Nav Items ─────────────────────────────────────────────────────────────────
const NAV = [
  { to:'/dashboard', Icon:LayoutDashboard, label:'Dashboard'     },
  { to:'/markets',   Icon:BarChart2,       label:'My Markets'    },
  { to:'/create',    Icon:PlusCircle,      label:'Create Market' },
  { to:'/analytics', Icon:TrendingUp,      label:'Analytics'     },
  { to:'/payouts',   Icon:Wallet,          label:'Payouts'       },
];

// ── Sidebar Content ───────────────────────────────────────────────────────────
function SidebarContent({ onClose }) {
  const { creator, logout } = useAuth();
  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
      {/* Logo */}
      <div style={{ padding:'18px 16px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <img src="/logo.svg" alt="NaijaPredict" style={{ width:34, height:34, borderRadius:8 }}/>
          <div>
            <div style={{ fontFamily:'system-ui,sans-serif', fontWeight:900, fontSize:14, color:'#fff', letterSpacing:.5 }}>NAIJAPREDICT</div>
            <div style={{ fontSize:9, color:T.green, fontWeight:700, letterSpacing:1.5, marginTop:1 }}>CREATOR PORTAL</div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={{ background:'none', border:'none', color:T.muted, cursor:'pointer', padding:4, display:'flex' }}>
            <X size={18}/>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex:1, padding:'10px 8px', overflowY:'auto' }}>
        {NAV.map(({ to, Icon, label }) => (
          <NavLink key={to} to={to} onClick={onClose}
            style={({ isActive }) => ({
              display:'flex', alignItems:'center', gap:10,
              padding:'11px 14px', borderRadius:10, marginBottom:3,
              fontSize:14, fontWeight:500, textDecoration:'none',
              color: isActive ? T.green : T.muted,
              background: isActive ? 'rgba(0,197,102,.08)' : 'transparent',
              borderLeft: isActive ? `3px solid ${T.green}` : '3px solid transparent',
              transition:'all .15s',
            })}>
            <Icon size={16} strokeWidth={1.8}/>{label}
          </NavLink>
        ))}
      </nav>

      {/* User */}
      <div style={{ padding:'14px 16px', borderTop:`1px solid ${T.border}` }}>
        <div style={{ fontSize:13, fontWeight:600, color:T.text, marginBottom:2 }}>{creator?.name || 'Creator'}</div>
        <div style={{ fontSize:11, color:T.muted, marginBottom:12 }}>@{creator?.username || 'unknown'}</div>
        <button onClick={logout}
          style={{ width:'100%', padding:'8px 12px', background:'rgba(255,255,255,.04)', border:`1px solid ${T.border}`, borderRadius:8, color:T.muted, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, fontFamily:'inherit' }}>
          <LogOut size={13}/> Sign out
        </button>
      </div>
    </div>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────
function Layout({ children }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:T.bg, color:T.text, fontFamily:"'Inter',system-ui,sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
        body{background:#07090a;color:#e8eaeb;font-family:'Inter',system-ui,sans-serif;-webkit-font-smoothing:antialiased}
        a{text-decoration:none;color:inherit}
        input,textarea,select{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:10px;padding:12px 14px;color:#e8eaeb;font-size:14px;width:100%;outline:none;font-family:inherit;transition:border .15s}
        input:focus,textarea:focus,select:focus{border-color:#00c566}
        select option{background:#0d1114}
        ::-webkit-scrollbar{width:4px}
        ::-webkit-scrollbar-track{background:#0d1114}
        ::-webkit-scrollbar-thumb{background:#2a3540;border-radius:2px}
      `}</style>

      {/* Desktop Sidebar */}
      <div style={{ width:240, flexShrink:0, display:'none' }} className="desktop-sidebar">
        <div style={{ position:'fixed', top:0, left:0, width:240, height:'100vh', background:T.bg2, borderRight:`1px solid ${T.border}`, zIndex:100 }}>
          <SidebarContent/>
        </div>
      </div>

      {/* Mobile Drawer Overlay */}
      {drawerOpen && (
        <div onClick={()=>setDrawerOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.75)', zIndex:299, backdropFilter:'blur(4px)' }}/>
      )}

      {/* Mobile Drawer */}
      <div style={{
        position:'fixed', top:0, left:0, width:280, height:'100vh',
        background:T.bg2, borderRight:`1px solid ${T.border}`,
        zIndex:300,
        transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition:'transform .28s cubic-bezier(.4,0,.2,1)',
      }}>
        <SidebarContent onClose={()=>setDrawerOpen(false)}/>
      </div>

      {/* Main */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

        {/* Mobile Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 16px', height:56, background:T.bg2, borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:50 }} className="mobile-header">
          <button onClick={()=>setDrawerOpen(true)}
            style={{ background:'none', border:'none', color:T.text, cursor:'pointer', padding:6, display:'flex', borderRadius:8 }}>
            <Menu size={20}/>
          </button>
          <span style={{ fontFamily:'system-ui,sans-serif', fontWeight:900, fontSize:15, letterSpacing:.5 }}>CREATOR PORTAL</span>
          <img src="/logo.svg" alt="" style={{ width:28, height:28, borderRadius:6 }}/>
        </div>

        {/* Page Content */}
        <div style={{ flex:1, padding:'20px 16px 60px', maxWidth:1100, width:'100%', margin:'0 auto' }}>
          {children}
        </div>
      </div>

      <style>{`
        @media(min-width:768px) {
          .desktop-sidebar { display:block !important; }
          .mobile-header { display:none !important; }
        }
        @media(min-width:768px) {
          div[style*="flex:1"][style*="flexDirection:column"] { margin-left:0; }
        }
      `}</style>
    </div>
  );
}

// ── Protected ─────────────────────────────────────────────────────────────────
function Protected({ children }) {
  const { creator } = useAuth();
  return creator ? children : <Navigate to="/login" replace/>;
}

// ── Pages ─────────────────────────────────────────────────────────────────────
import LoginPage    from './pages/LoginPage';
import Dashboard    from './pages/Dashboard';
import Markets      from './pages/Markets';
import CreateMarket from './pages/CreateMarket';
import Analytics    from './pages/Analytics';
import Payouts      from './pages/Payouts';

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage/>}/>
          <Route path="/*" element={
            <Protected>
              <Layout>
                <Routes>
                  <Route path="/dashboard"  element={<Dashboard/>}/>
                  <Route path="/markets"    element={<Markets/>}/>
                  <Route path="/create"     element={<CreateMarket/>}/>
                  <Route path="/analytics"  element={<Analytics/>}/>
                  <Route path="/payouts"    element={<Payouts/>}/>
                  <Route path="*"           element={<Navigate to="/dashboard" replace/>}/>
                </Routes>
              </Layout>
            </Protected>
          }/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
